import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';
import {useCallback, useEffect, useRef, useState} from 'react';

const DISMISS_KEY = 'mixify-pwa-install-dismissed';
const STARTUP_DELAY_MS = 1200;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'}>;
}

function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & {standalone?: boolean}).standalone === true
  );
}

export function PwaInstallPrompt() {
  const isMobile = useIsMobileMediaQuery();
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const hasShownRef = useRef(false);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  }, []);

  const tryShow = useCallback(() => {
    if (
      hasShownRef.current ||
      localStorage.getItem(DISMISS_KEY) ||
      isStandalone()
    ) {
      return;
    }
    hasShownRef.current = true;
    setVisible(true);
  }, []);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) || isStandalone()) {
      return;
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      if (isMobile) {
        tryShow();
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () =>
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, [isMobile, tryShow]);

  useEffect(() => {
    if (!isMobile || localStorage.getItem(DISMISS_KEY) || isStandalone()) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (deferredPrompt.current) {
        tryShow();
      } else if (isIos()) {
        setShowIosHint(true);
        tryShow();
      }
    }, STARTUP_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isMobile, tryShow]);

  if (!visible) {
    return null;
  }

  const handleYes = async () => {
    const prompt = deferredPrompt.current;
    if (prompt) {
      await prompt.prompt();
      await prompt.userChoice;
      deferredPrompt.current = null;
    }
    dismiss();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-24">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        aria-hidden
      />
      <div
        role="dialog"
        aria-labelledby="pwa-install-title"
        aria-describedby="pwa-install-desc"
        className="relative z-10 w-full max-w-[min(100%,360px)] rounded-2xl border border-divider-lighter bg-elevated p-24 shadow-[0_24px_64px_rgba(0,0,0,0.28)] dark:shadow-[0_24px_64px_rgba(0,0,0,0.55)]"
      >
        <h2
          id="pwa-install-title"
          className="text-center text-base font-semibold leading-snug md:text-lg"
        >
          <Trans message="Install Mixify for uninterrupted playback" />
        </h2>
        {showIosHint && (
          <p
            id="pwa-install-desc"
            className="mt-12 text-center text-sm leading-relaxed text-muted"
          >
            <Trans message="Tap Share, then Add to Startscreen." />
          </p>
        )}
        <div className="mt-24 flex gap-12">
          <Button
            variant="outline"
            color="primary"
            className="min-h-44 flex-1"
            onClick={dismiss}
          >
            <Trans message="No" />
          </Button>
          <Button
            variant="flat"
            color="primary"
            className="min-h-44 flex-1"
            onClick={handleYes}
          >
            <Trans message="Yes" />
          </Button>
        </div>
      </div>
    </div>
  );
}
